import type { ChatMessage } from 'lib/twitchChatParse'

export const YOUTUBE_VIDEO_ID_SHAPE = /^[A-Za-z0-9_-]{11}$/
export const YOUTUBE_CHANNEL_ID_SHAPE = /^UC[A-Za-z0-9_-]{22}$/
export const TWITCH_LOGIN_SHAPE = /^[a-z0-9_]{1,25}$/

export const YOUTUBE_HOME_URL = 'https://www.youtube.com/'
export const TWITCH_HOME_URL = 'https://www.twitch.tv/'

export const SOURCE_MARK_LABELS = Object.freeze({
  youtubeStream: 'Open this YouTube live stream in a new tab',
  youtubeChannel: 'Open this YouTube channel in a new tab',
  youtubeHome: 'Open YouTube in a new tab',
  twitchChannel: 'Open this Twitch channel in a new tab',
  twitchHome: 'Open Twitch in a new tab'
})

export interface SourceMarkTarget {
  url: string
  label: string
}

export const youtubeWatchUrl = (videoId: string): string =>
  `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`

const shaped = (value: unknown, shape: RegExp): value is string =>
  typeof value === 'string' && shape.test(value)

export const youtubeMarkTarget = (ids: { videoId?: string, channelId?: string }): SourceMarkTarget => {
  if (shaped(ids.videoId, YOUTUBE_VIDEO_ID_SHAPE)) {
    return { url: youtubeWatchUrl(ids.videoId), label: SOURCE_MARK_LABELS.youtubeStream }
  }
  if (shaped(ids.channelId, YOUTUBE_CHANNEL_ID_SHAPE)) {
    return {
      url: `https://www.youtube.com/channel/${encodeURIComponent(ids.channelId)}`,
      label: SOURCE_MARK_LABELS.youtubeChannel
    }
  }
  return { url: YOUTUBE_HOME_URL, label: SOURCE_MARK_LABELS.youtubeHome }
}

export const twitchMarkTarget = (login: string | undefined): SourceMarkTarget => {
  if (shaped(login, TWITCH_LOGIN_SHAPE)) {
    return { url: `https://www.twitch.tv/${encodeURIComponent(login)}`, label: SOURCE_MARK_LABELS.twitchChannel }
  }
  return { url: TWITCH_HOME_URL, label: SOURCE_MARK_LABELS.twitchHome }
}

export const chatMarkTarget = (message: ChatMessage): SourceMarkTarget =>
  message.platform === 'YOUTUBE'
    ? youtubeMarkTarget({ videoId: message.sourceVideoId, channelId: message.sourceChannelId })
    : twitchMarkTarget(message.channel)

export const voteMarkTarget = (platform: 'twitch' | 'youtube'): SourceMarkTarget =>
  platform === 'twitch'
    ? { url: TWITCH_HOME_URL, label: SOURCE_MARK_LABELS.twitchHome }
    : { url: YOUTUBE_HOME_URL, label: SOURCE_MARK_LABELS.youtubeHome }
